/**
 * Campaign tracking capture hook
 * Created: 2026-03-07 - Reads ?ref=CODE from URL, stores attribution, fires link_click event
 * Updated: 2026-03-16 - Store ref in sessionStorage as backup; restore attribution from sessionStorage;
 *                        delay URL cleanup until after attribution is confirmed stored
 */

import { useEffect, useRef } from "react";

const EDGE_URL = "https://sdvakfhmoaoucmhbhwvy.supabase.co/functions/v1/track-event";
const STORAGE_KEY = "tracking_attribution";
const VISITOR_KEY = "tracking_visitor_id";
const SESSION_REF_KEY = "tracking_ref_code";

function getOrCreateVisitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  let id = sessionStorage.getItem("tracking_session_id");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("tracking_session_id", id);
  }
  return id;
}

export function useTrackingCapture() {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");

    if (code) {
      // Store attribution in localStorage (primary)
      const attribution = {
        code,
        captured_at: Date.now(),
        landing_page: window.location.pathname,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));

      // Store ref code in sessionStorage (backup — survives within same tab)
      sessionStorage.setItem(SESSION_REF_KEY, code);

      // Fire link_click event
      const visitorId = getOrCreateVisitorId();
      const sessionId = getSessionId();

      fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          event_type: "link_click",
          visitor_id: visitorId,
          session_id: sessionId,
          referrer: document.referrer || null,
          page_url: window.location.href,
        }),
      }).catch(() => {}); // Fire-and-forget

      // Delay URL cleanup to ensure attribution is stored before stripping ref
      setTimeout(() => {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete("ref");
        const cleanUrl = currentParams.toString()
          ? `${window.location.pathname}?${currentParams}`
          : window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
      }, 500);

      return; // ref found and handled
    }

    // No ?ref= in URL — check if sessionStorage has a ref code but localStorage lost it
    const sessionRef = sessionStorage.getItem(SESSION_REF_KEY);
    if (sessionRef) {
      const existingAttribution = localStorage.getItem(STORAGE_KEY);
      if (!existingAttribution) {
        // Re-establish attribution from sessionStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          code: sessionRef,
          captured_at: Date.now(),
          landing_page: window.location.pathname,
        }));
      }
    }

    // Ensure visitor_id exists even for organic visits
    getOrCreateVisitorId();
  }, []);
}
